import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Table,
    Input,
    Typography,
    Card,
    Spin,
    App,
    Tag,
    Select,
    DatePicker,
    Button,
    Row,
    Col,
    Skeleton
} from 'antd';
import {
    SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {authService, salesService} from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import { IconDotFilled, IconDotPartially, IconDotUnfilled } from "../../../../components/icons";
import SalesExportModal from "../../components/SalesExportModal";

const { Text } = Typography;

const { RangePicker } = DatePicker;
const { Option } = Select;

const TableSkeleton = () => {
    const skeletonRows = Array(20).fill(null).map((_, index) => (
        <tr key={index}>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Input active size="small" style={{ width: 60, height: 17 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Input active size="small" style={{ width: 100, height: 17 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Input active size="small" style={{ width: 50, height: 17 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Input active size="small" style={{ width: 132, height: 17 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Input active size="small" style={{ width: 120, height: 17 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Button active size="small" style={{ width: 93, height: 22 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Button active size="small" style={{ width: 93, height: 22 }} />
            </td>
            <td style={{ padding: '12px 8px' }}>
                <Skeleton.Button active size="small" style={{ width: 130, height: 22 }} />
            </td>
        </tr>
    ));

    return (
        <div style={{border: '1px solid #f0f0f0', borderRadius: '6px'}}>
            <style>
                {`
               .table-horizontal-borders .ant-table-tbody > tr > td {
                   border-left: none !important;
                   border-right: none !important;
               }
               .table-horizontal-borders .ant-table-thead > tr > th {
                   border-left: none !important;
                   border-right: none !important;
               }
               .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                   background-color: #f5f5f5 !important;
               }
           `}
            </style>
            <table style={{width: '100%', borderCollapse: 'collapse'}}
                   className="table-horizontal-borders">
                <thead>
                <tr style={{borderBottom: '1px solid #f0f0f0', background: '#fafafa'}}>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>ID</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>ID заказа</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Платформа</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Дата продажи</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Сумма</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Статус оплаты</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Статус выполнения</th>
                    <th style={{padding: '12px 8px', textAlign: 'left', fontWeight: 600}}>Статус возврата</th>
                </tr>
                </thead>
                <tbody>
                {skeletonRows}
                </tbody>
            </table>
        </div>
    );
};

// Функция для форматирования даты в формате "1 июля в 23:11"
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';

    const date = new Date(dateString);
    const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];

    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day} ${month} в ${hours}:${minutes}`;
};

// Функция для форматирования суммы
const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

// Иконки для статусов
const StatusIcons = {
    IconDotFilled: IconDotFilled,
    IconDotPartially: IconDotPartially,
    IconDotUnfilled: IconDotUnfilled,
};

const PLATFORM_OPTIONS = [
    { value: 'pos', label: 'POS' },
    { value: 'glovo', label: 'Glovo' },
    { value: 'wolt', label: 'Wolt' },
    { value: 'yandex', label: 'Yandex' }
];

const PAYMENT_STATUS_OPTIONS = [
    { value: 'paid', label: 'Оплачено' },
    { value: 'partially_refunded', label: 'Частично возвращено' },
    { value: 'refunded', label: 'Возвращено' },
    { value: 'pending', label: 'Ожидается оплата' }
];

const FULFILLMENT_STATUS_OPTIONS = [
    { value: 'fulfilled', label: 'Собрано' },
    { value: 'unfulfilled', label: 'Не собрано' },
    { value: 'partially_fulfilled', label: 'Частично собрано' },
    { value: 'scheduled', label: 'Ожидает сборки' }
];

const RETURN_STATUS_OPTIONS = [
    { value: 'is_progress', label: 'Возврат в процессе' },
    { value: 'requested', label: 'Возврат запрошен' },
    { value: 'closed', label: 'Возврат завершён' }
];

// Функция для рендеринга статуса оплаты
const renderPaymentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'paid': { color: 'default', text: 'Оплачено', icon: StatusIcons.IconDotFilled },
        'partially_refunded': { color: 'purple', text: 'Частично возвращено', icon: StatusIcons.IconDotPartially },
        'refunded': { color: 'default', text: 'Возвращено', icon: StatusIcons.IconDotFilled },
        'pending': { color: 'orange', text: 'Ожидается оплата', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса выполнения
const renderFulfillmentStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'fulfilled': { color: 'default', text: 'Собрано', icon: StatusIcons.IconDotFilled },
        'unfulfilled': { color: 'gold', text: 'Не собрано', icon: StatusIcons.IconDotFilled },
        'partially_fulfilled': { color: 'orange', text: 'Частично собрано', icon: StatusIcons.IconDotPartially },
        'scheduled': { color: 'purple', text: 'Ожидает сборки', icon: StatusIcons.IconDotUnfilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

// Функция для рендеринга статуса возврата
const renderReturnStatus = (status) => {
    if (!status) return null;

    const statusConfig = {
        'is_progress': { color: 'blue', text: 'Возврат в процессе', icon: StatusIcons.IconDotUnfilled },
        'requested': { color: 'blue', text: 'Возврат запрошен', icon: StatusIcons.IconDotUnfilled },
        'closed': { color: 'default', text: 'Возврат завершён', icon: StatusIcons.IconDotFilled }
    };

    const config = statusConfig[status];
    if (!config) return null;

    const IconComponent = config.icon;

    return (
        <Tag color={config.color} icon={<IconComponent />}>
            {config.text}
        </Tag>
    );
};

const SalesList = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { message } = App.useApp();

    const STORAGE_KEY = 'sales_list_state';

    const saveStateToSession = (filters, searchText, cursor = null, salesData = []) => {
        // Сохраняем только если есть данные для сохранения
        if (salesData.length > 0 || searchText || Object.values(filters).some(v => v !== null)) {
            const state = {
                filters,
                searchText,
                cursor,
                salesData,
                timestamp: Date.now()
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        }
    };

    // Функция для сохранения состояния при переходе
    const saveStateOnNavigation = () => {
        saveStateToSession(filters, searchText);
    };
    const getStateFromSession = () => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (!saved) return null;

            const state = JSON.parse(saved);
            // Проверяем, не старше ли состояние 30 минут
            if (Date.now() - state.timestamp > 1800000) {
                sessionStorage.removeItem(STORAGE_KEY);
                return null;
            }

            return state;
        } catch {
            return null;
        }
    };

    const savedState = getStateFromSession();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState(savedState?.searchText || '');
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const [filters, setFilters] = useState(savedState?.filters || {
        sale_date: null,
        platform: null,
        return_status: null,
        payment_status: null,
        fulfillment_status: null
    });

    const observer = useRef();
    const lastSaleElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMoreSales();
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, error]);


    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Продажи');

        if (authService.hasPermission('pos:sales:export')) {
            setRightContent(
                <Button
                    onClick={() => setExportModalOpen(true)}
                    ghost
                >
                    Экспорт
                </Button>
            );
        }

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Загрузка записей при первой загрузке и при изменении поиска
    useEffect(() => {
        setError(false);
        if (savedState) {
            // Если есть сохраненный поиск, устанавливаем его
            if (savedState.searchText) {
                setSearchText(savedState.searchText);
            }
        } else {
            fetchSales();
        }
    }, []);

    // useEffect для поиска
    useEffect(() => {
        if (searchText !== (savedState?.searchText || '')) {
            const timeoutId = setTimeout(() => {
                setError(false);
                fetchSales();
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [searchText]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setError(false);
            fetchSales();
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchText]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setError(false);
            fetchSales();
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [filters]);

    // Обработчик изменения поля поиска
    const handleSearch = (e) => {
        setSearchText(e.target.value);
        saveStateToSession(filters, e.target.value);
    };

    // Загрузка записей
    const fetchSales = async () => {
        try {
            setLoading(true);
            setSales([]);
            setNextCursor(null);
            setHasMore(true);

            const requestParams = {};

            if (searchText) {
                requestParams.search = searchText;
            }

            const activeFilters = {};

            if (filters.sale_date) {
                activeFilters.sale_date = filters.sale_date;
            }
            if (filters.platform) {
                activeFilters.platform = filters.platform;
            }
            if (filters.return_status) {
                activeFilters.return_status = filters.return_status;
            }
            if (filters.payment_status) {
                activeFilters.payment_status = filters.payment_status;
            }
            if (filters.fulfillment_status) {
                activeFilters.fulfillment_status = filters.fulfillment_status;
            }

            if (Object.keys(activeFilters).length > 0) {
                requestParams.filters = JSON.stringify(activeFilters);
            }

            const response = await salesService.getSales(requestParams);

            if (response && response.status === 'success') {
                setSales(response.data.items);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить список продаж');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке продаж');
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Загрузка дополнительных продаж при прокрутке
    const loadMoreSales = async () => {
        if (!hasMore || loading || error) return;

        try {
            setLoading(true);

            const requestParams = {
                cursor: nextCursor
            };

            if (searchText) {
                requestParams.search = searchText;
            }

            const activeFilters = {};

            if (filters.sale_date) {
                activeFilters.sale_date = filters.sale_date;
            }
            if (filters.platform) {
                activeFilters.platform = filters.platform;
            }
            if (filters.return_status) {
                activeFilters.return_status = filters.return_status;
            }
            if (filters.payment_status) {
                activeFilters.payment_status = filters.payment_status;
            }
            if (filters.fulfillment_status) {
                activeFilters.fulfillment_status = filters.fulfillment_status;
            }

            if (Object.keys(activeFilters).length > 0) {
                requestParams.filters = JSON.stringify(activeFilters);
            }

            const response = await salesService.getSales(requestParams);

            if (response && response.status === 'success') {
                setSales(prev => [...prev, ...response.data.items]);
                setNextCursor(response.data.pagination.next_cursor);
                setHasMore(response.data.pagination.has_more);
                setTotal(response.data.pagination.total);
            } else {
                message.error(response?.message || 'Не удалось загрузить больше продаж');
            }
        } catch (error) {
            setError(true);
            message.error(error.message || 'Ошибка при загрузке дополнительных продаж');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (filterKey, value) => {
        const newFilters = {
            ...filters,
            [filterKey]: value
        };
        setFilters(newFilters);
        saveStateToSession(newFilters, searchText);
    };

    const handleDateRangeChange = (dates) => {
        const dateFilter = dates && dates.length === 2 ? {
            from: dates[0].format('YYYY-MM-DD'),
            to: dates[1].format('YYYY-MM-DD')
        } : null;

        const newFilters = {
            ...filters,
            sale_date: dateFilter
        };
        setFilters(newFilters);
        saveStateToSession(newFilters, searchText);
    };

    const clearSearch = () => {
        setSearchText('');
        saveStateToSession(filters, '');
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (text) => (
                <Text strong>
                    #{text}
                </Text>
            ),
        },
        {
            title: 'ID заказа',
            dataIndex: 'platform_order_id',
            key: 'platform_order_id',
            width: 120,
            render: (text) => text,
        },
        {
            title: 'Платформа',
            dataIndex: 'platform',
            key: 'platform',
            width: 60,
            render: (platform) => {
                const platformMap = {
                    'pos': 'POS',
                    'glovo': 'Glovo',
                    'wolt': 'Wolt',
                    'yandex': 'Yandex'
                };
                return platformMap[platform] || platform;
            },
        },
        {
            title: 'Дата продажи',
            dataIndex: 'sale_date',
            key: 'sale_date',
            width: 100,
            render: (text) => formatDate(text),
        },
        {
            title: 'Сумма',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 90,
            render: (amount) => `${formatAmount(amount)} ₸`,
        },
        {
            title: 'Статус оплаты',
            dataIndex: 'payment_status',
            key: 'payment_status',
            width: 110,
            render: (status) => renderPaymentStatus(status),
        },
        {
            title: 'Статус выполнения',
            dataIndex: 'fulfillment_status',
            key: 'fulfillment_status',
            width: 100,
            render: (status) => renderFulfillmentStatus(status),
        },
        {
            title: 'Статус возврата',
            dataIndex: 'return_status',
            key: 'return_status',
            width: 100,
            render: (status) => renderReturnStatus(status),
        },
    ];

    // Создаем данные для таблицы
    const tableData = sales.map((sale, index) => ({
        ...sale,
        key: sale.id
    }));

    return (
        <>
            <Card style={{ marginBottom: 16, minHeight: '100vh' }} >
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 16]}>
                        <Col span={16}>
                            <Input
                                placeholder="Поиск по ID, UID"
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={handleSearch}
                                allowClear
                                onClear={clearSearch}
                            />
                        </Col>
                        <Col span={8}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Typography.Text>
                                    Всего: {total}
                                </Typography.Text>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={[16, 8]} style={{ marginTop: 16 }}>
                        <Col span={4}>
                            <Select
                                placeholder="Платформа"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('platform', value)}
                                value={filters.platform}
                            >
                                {PLATFORM_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={8}>
                            <RangePicker
                                placeholder={['Дата с', 'Дата по']}
                                format="DD.MM.YYYY"
                                onChange={handleDateRangeChange}
                                style={{ width: '100%' }}
                                allowClear
                                disabledDate={(current) => current && current > dayjs().endOf('day')}
                                value={filters.sale_date ? [
                                    dayjs(filters.sale_date.from),
                                    dayjs(filters.sale_date.to)
                                ] : null}
                            />
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="Статус оплаты"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('payment_status', value)}
                                value={filters.payment_status}
                            >
                                {PAYMENT_STATUS_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="Статус выполнения"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('fulfillment_status', value)}
                                value={filters.fulfillment_status}
                            >
                                {FULFILLMENT_STATUS_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                placeholder="Статус возврата"
                                allowClear
                                style={{ width: '100%' }}
                                onChange={(value) => handleFilterChange('return_status', value)}
                                value={filters.return_status}
                            >
                                {RETURN_STATUS_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    </Row>
                </div>

                {initialLoading ? (
                    <TableSkeleton />
                ) : (
                    <>
                        <style>
                            {`
               .table-horizontal-borders .ant-table-tbody > tr > td {
                   border-left: none !important;
                   border-right: none !important;
               }
               .table-horizontal-borders .ant-table-thead > tr > th {
                   border-left: none !important;
                   border-right: none !important;
               }
               .table-horizontal-borders .ant-table-tbody > tr:hover > td {
                   background-color: #f5f5f5 !important;
               }
           `}
                        </style>
                        <Table
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            size="middle"
                            bordered={false}
                            className="table-horizontal-borders"
                            tableLayout="fixed"
                            scroll={{ x: 1000 }}
                            onRow={(record) => ({
                                onClick: () => {
                                    saveStateOnNavigation();
                                    navigate(`/sales/${record.id}`);
                                },
                                style: { cursor: 'pointer' }
                            })}
                        />
                        {loading && !initialLoading && (
                            <div style={{textAlign: 'center', padding: '20px 0'}}>
                                <Spin/>
                            </div>
                        )}
                        {hasMore && (
                            <div
                                ref={lastSaleElementRef}
                                style={{height: 10, width: '100%'}}
                            />
                        )}
                        {!hasMore && sales.length > 0 && (
                            <div style={{textAlign: 'center', padding: '10px'}}>
                                <Typography.Text type="secondary">
                                    Больше записей нет
                                </Typography.Text>
                            </div>
                        )}
                    </>
                )}
            </Card>
            <SalesExportModal
                open={exportModalOpen}
                onCancel={() => setExportModalOpen(false)}
            />
        </>
    );
};

export default SalesList;